import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Grid,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Stack,
} from '@mui/material';
import { Input, CommonModal, Select } from '../../Common';
import { trakrefBlue } from '../../../color_utils';
import { getProp, setProp, setOrClearProp, extractTagReferences } from './rulesetUtils';
import RuleExpressionBuilder from './RuleExpressionBuilder';
import { emptyGroup, generateExpression, expressionsMatch, parseExpression } from './expressionCatalog';

const OPERATOR_OPTIONS = [
    { value: 'And', name: 'And' },
    { value: 'AndAlso', name: 'AndAlso' },
    { value: 'Or', name: 'Or' },
    { value: 'OrElse', name: 'OrElse' },
];

const initialFormValue = {
    ruleName: '',
    operator: '',
    errorMessage: '',
};

const RuleEditModal = ({
    node,
    modalOpen,
    onClose,
    onApplied,
    ruleNames,
    readOnly
}) => {
    const [mode, setMode] = useState('builder');
    const [form, setForm] = useState(initialFormValue);
    const [ast, setAst] = useState(emptyGroup());
    const [parseNotice, setParseNotice] = useState(null);
    const [builderError, setBuilderError] = useState(null);
    const [rawJson, setRawJson] = useState('');
    const [rawError, setRawError] = useState(null);

    useEffect(() => {
        if (!node)
            return;

        setForm({
            ruleName: node.name === '(unnamed rule)' ? '' : node.name,
            operator: node.operator,
            errorMessage: node.errorMessage,
        });
        setRawJson(JSON.stringify(node.ref, null, 2));
        setRawError(null);
        setBuilderError(null);

        // reconstruct the builder conditions from the expression itself —
        // nothing extra is stored on the rule JSON. Expressions the catalog
        // can't map open in Raw JSON mode instead.
        const parsed = parseExpression(node.expression);
        if (parsed) {
            setAst(parsed);
            setMode('builder');
            const { expression } = generateExpression(parsed);
            setParseNotice(expressionsMatch(expression, node.expression) ? null : 'reconstructed');
        } else {
            setAst(emptyGroup());
            setMode(node.expression ? 'json' : 'builder');
            setParseNotice(node.expression ? 'unmapped' : null);
        }
    }, [node]);

    const onChangeForm = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyChanges = () => {
        if (mode === 'json') {
            let parsed;
            try {
                parsed = JSON.parse(rawJson);
            } catch (error) {
                setRawError(`Invalid JSON: ${error.message}`);
                return;
            }
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                setRawError('A rule must be a JSON object.');
                return;
            }
            Object.keys(node.ref).forEach(key => delete node.ref[key]);
            Object.assign(node.ref, parsed);
        } else {
            if (!form.ruleName.trim()) {
                setBuilderError('Rule name is required.');
                return;
            }
            const { expression, errors } = generateExpression(ast);
            if (errors.length > 0) {
                setBuilderError(errors[0]);
                return;
            }
            setProp(node.ref, 'Expression', expression);
            setProp(node.ref, 'RuleName', form.ruleName);
            setOrClearProp(node.ref, 'Operator', form.operator);
            setOrClearProp(node.ref, 'ErrorMessage', form.errorMessage);
        }

        onApplied();
        onClose();
    };

    // Tag references the expression-in-progress makes that match no rule in the
    // ruleset: renamed or mistyped targets can never match a current tag (the
    // engine writes tags under rule names). A warning rather than a block — the
    // referenced rule might be added next; SAVE RULESET is the hard gate.
    const danglingReferences = useMemo(() => {
        let expression = '';
        if (mode === 'json') {
            try {
                expression = getProp(JSON.parse(rawJson), 'Expression') || '';
            } catch {
                return [];
            }
        } else {
            expression = generateExpression(ast).expression || '';
        }
        const knownNames = new Set(ruleNames || []);
        return [...new Set(extractTagReferences(expression))].filter(name => !knownNames.has(name));
    }, [mode, rawJson, ast, ruleNames]);

    if (!node)
        return null;

    const hasChildren = node.children && node.children.length > 0;
    const tagOptions = (ruleNames || []).filter(name => name !== node.name);

    return (
        <CommonModal
            open={modalOpen}
            close={onClose}
            header={readOnly ? "View Rule" : "Edit Rule"}
            width={mode === 'builder' ? '860px' : '640px'}
            maxWidth={mode === 'builder' ? '860px' : '640px'}
        >
            <Grid container pl={4} pr={4}>
                <Grid item xs={12} lg={12} mb={2} pt={1}>
                    <Stack spacing={"15px"}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={mode}
                            onChange={(e, newMode) => {
                                if (!newMode)
                                    return;
                                if (newMode === 'json') {
                                    setRawJson(JSON.stringify(node.ref, null, 2));
                                    setRawError(null);
                                }
                                setBuilderError(null);
                                setMode(newMode);
                            }}
                            sx={{ alignSelf: 'center' }}
                        >
                            <ToggleButton value="builder" sx={{ px: 3 }}>Builder</ToggleButton>
                            <ToggleButton value="json" sx={{ px: 3 }}>Raw JSON</ToggleButton>
                        </ToggleButtonGroup>
                        {mode === 'json' && (
                            <Input
                                fullWidth
                                multiLine
                                rows={16}
                                variant="outlined"
                                label="Rule JSON"
                                value={rawJson}
                                error={!!rawError}
                                helperText={rawError || ''}
                                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '13px' } }}
                                onChange={e => {
                                    setRawError(null);
                                    setRawJson(e.target.value);
                                }}
                            />
                        )}
                        {mode !== 'json' && (
                            <>
                                <Input
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Rule Name"
                                    value={form.ruleName}
                                    error={builderError === 'Rule name is required.' && !form.ruleName.trim()}
                                    onChange={e => onChangeForm("ruleName", e.target.value)}
                                />
                                {(hasChildren || form.operator) && (
                                    <Select
                                        label={"Operator"}
                                        variant={"outlined"}
                                        options={OPERATOR_OPTIONS}
                                        value={form.operator}
                                        onChange={e => onChangeForm("operator", e.target.value)}
                                    />
                                )}
                                {parseNotice === 'reconstructed' && (
                                    <Alert severity="info" sx={{ fontSize: '12px' }}>
                                        These conditions were reconstructed from the stored expression{readOnly
                                            ? ' for display.'
                                            : ". Applying will rewrite the expression with the builder's formatting."}
                                    </Alert>
                                )}
                                {parseNotice === 'unmapped' && (
                                    <Alert severity="warning" sx={{ fontSize: '12px' }}>
                                        {readOnly
                                            ? "This expression couldn't be mapped to builder conditions — switch to Raw JSON to see it."
                                            : "The existing expression couldn't be mapped to builder conditions, so the builder starts empty. Applying will overwrite the expression — use Raw JSON mode to keep it."}
                                    </Alert>
                                )}
                                <RuleExpressionBuilder
                                    value={ast}
                                    onChange={next => {
                                        setBuilderError(null);
                                        setAst(next);
                                    }}
                                    tagOptions={tagOptions}
                                />
                                {builderError && (
                                    <Alert severity="error" sx={{ fontSize: '12px' }}>{builderError}</Alert>
                                )}
                                <Input
                                    fullWidth
                                    multiLine
                                    rows={2}
                                    variant="outlined"
                                    label="Error Message"
                                    value={form.errorMessage}
                                    onChange={e => onChangeForm("errorMessage", e.target.value)}
                                />
                            </>
                        )}
                        {danglingReferences.length > 0 && (
                            <Alert severity="warning" sx={{ fontSize: '12px' }}>
                                This expression references {danglingReferences.map(name => `"${name}"`).join(', ')},
                                which {danglingReferences.length === 1 ? 'matches' : 'match'} no rule in this
                                ruleset — renamed or mistyped? Tags are written under rule names, so this
                                condition can never match a current tag.
                            </Alert>
                        )}
                        {!readOnly && (
                            <Button
                                fullWidth
                                size='large'
                                variant='contained'
                                onClick={applyChanges}
                                style={{
                                    backgroundColor: trakrefBlue,
                                    marginTop: "25px",
                                    marginBottom: "15px"
                                }}
                            >
                                <Typography sx={{ padding: '1% 0%', color: '#fff', fontSize: 17, fontWeight: 400 }}>
                                    APPLY CHANGES
                                </Typography>
                            </Button>
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </CommonModal>
    );
};

export default RuleEditModal;
